import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Form, Formik } from "formik";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CheckoutSession } from "lib/api/checkout";
import { Button, Image, Theme, colors } from "@literati/public-ui-shared-react";
import LegacyThemeProvider from "components/Layout/DefaultThemeProvider";
import GuidedSignUpRadioGroup, {
  GuidedSignUpRadioGroupOption,
  GuidedSignUpRadioVariant,
} from "components/GuidedSignUp/GuidedSignUpRadioGroup";
import { SubscriptionPlanCamelCase } from "lib/api/subscription";
import { GuidedSignUpRadioBoxyClassName } from "components/GuidedSignUp/GuidedSignUpRadioBoxy";
import CheckoutStripeForm from "components/checkout/CheckoutStripeForm";
import CheckoutPayPalButton from "components/checkout/CheckoutPayPalButton";
import { CheckoutSectionCardHeader } from "components/Step/StepPrimitives";
import usePayment, { UsePaymentArgs } from "components/Payment/usePayment";
import PaymentFormDefaultPayment, {
  DefaultPaymentMethod,
} from "components/Payment/PaymentFormDefaultPayment";
import PaymentFormLegalEnrollment from "components/Payment/PaymentFormLegalEnrollment";
import {
  PaymentMethodOptions,
  PaymentMethod,
} from "components/Payment/paymentTypes";
import {
  LegalEnrollmentKey,
  LegalEnrollmentValues,
  PaymentMethodKey,
  PaymentMethodSubmit,
  PaymentMethodValues,
  PaymentMethodValuesPartial,
} from "components/Payment/paymentFormTypes";
import { assetUrl } from "utils";
import { PlanPrepaidPricing } from "components/PlanSelection/planSelectionUtil";
import CheckoutExpressElement from "components/checkout/CheckoutExpressElement";

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ""
);

const PAYMENT_METHOD_TOGGLE_SECTION_FORM_ID = "payment-form-payment-method";

export type PaymentFormId =
  | "default-payment-method"
  | "paypal-form"
  | "stripe-form";

export const PAYMENT_FORM_ID_MAP: Record<PaymentMethod, PaymentFormId> = {
  default: "default-payment-method",
  paypal: "paypal-form", // fake form-id, signal to hide footer
  stripe: "stripe-form",
} as const;

const CLASS_BASE = "payment-form-payment-method";
export const PaymentFormPaymentMethodClassName = {
  BASE: CLASS_BASE,
} as const;

const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ExistingMethodToggle = styled.div`
  margin: 1rem 0 0;
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  border-radius: 4px;
  border: 1px solid ${colors.black};
`;

const StyledGuidedSignUpRadioGroup = styled(GuidedSignUpRadioGroup)`
  margin: 1rem 0 0;

  .${GuidedSignUpRadioBoxyClassName.LABEL} {
    width: 100%;
  }

  .${GuidedSignUpRadioBoxyClassName.MAIN_LABEL} {
    font-size: 0.875rem;
    font-weight: 500;
    line-height: auto;
  }
`;

export type OnPaymentMethodClick = (
  option: GuidedSignUpRadioGroupOption
) => void;

export interface PaymentFormPaymentMethodProps extends PaymentMethodOptions {
  className?: string;
  defaultPaymentMethod: DefaultPaymentMethod;
  hasDefaultPaymentMethod: boolean;
  onDisableSubmit: (disable: boolean) => void;
  onEnrollmentValuesChange?: (values: LegalEnrollmentValues) => void;
  onPaymentMethodChange: (paymentMethod: PaymentMethod) => void;
  onRadioClick?: (option: GuidedSignUpRadioGroupOption) => void;
  onSubmitStateChange?: (submitting: boolean) => void;
  onValuesChange?: (values: PaymentMethodValues) => void;
  paymentMethod: PaymentMethod;
  plan: SubscriptionPlanCamelCase;
  selectedPlanPrepaidPricing?: PlanPrepaidPricing | null;
  values: PaymentMethodValues;
}

export const PaymentFormPaymentMethod = ({
  className = "",
  defaultPaymentMethod,
  error,
  handleError,
  handleToken,
  hasDefaultPaymentMethod,
  onDisableSubmit,
  onEnrollmentValuesChange,
  onPaymentMethodChange,
  onRadioClick,
  onSubmitStateChange,
  onValuesChange,
  orderTotal,
  paymentMethod,
  plan,
  values,
}: PaymentFormPaymentMethodProps) => {
  const cls = PaymentFormPaymentMethodClassName;

  useEffect(() => {
    onValuesChange?.(values);
  }, [onValuesChange, values]);

  return (
    <Theme>
      <CheckoutExpressElement
        amount={orderTotal}
        onToken={handleToken}
        onError={handleError}
      />
      {paymentMethod !== "default" ? (
        <CheckoutSectionCardHeader>Payment Methods</CheckoutSectionCardHeader>
      ) : null}
      {hasDefaultPaymentMethod && paymentMethod !== "default" ? (
        <ExistingMethodToggle>
          <p>
            This credit card will become the payment method for all of your
            Literati subscriptions.
          </p>
          <Button
            css="margin: 1rem 0 0"
            onClick={() => onPaymentMethodChange("default")}
            type="button"
            variant="link"
          >
            Keep existing method
          </Button>
        </ExistingMethodToggle>
      ) : null}
      {paymentMethod !== "default" ? (
        <Form
          className={`${cls.BASE} ${className}`}
          id={PAYMENT_METHOD_TOGGLE_SECTION_FORM_ID}
        >
          <StyledGuidedSignUpRadioGroup
            name={PaymentMethodKey.PAYMENT_METHOD}
            onClick={onRadioClick}
            options={[
              {
                id: "payment-method-paypal",
                value: "paypal",
                label: (
                  <StyledDiv>
                    <span>PayPal</span>
                    <Image
                      alt=""
                      src="https://siren-images.literati.com/documents/paypal-logo-letters.svg"
                      height={20}
                      width={18}
                    />
                  </StyledDiv>
                ),
              },
              {
                id: "payment-method-credit-card",
                value: "stripe",
                label: (
                  <StyledDiv>
                    <span>Credit Card</span>
                    <Image
                      alt=""
                      src="https://siren-images.literati.com/documents/credit_card_logos.svg"
                      height={16}
                      width={110}
                    />
                  </StyledDiv>
                ),
              },
            ]}
            variant={GuidedSignUpRadioVariant.boxy}
          />
        </Form>
      ) : null}
      <CheckoutSectionCardHeader
        css={paymentMethod !== "default" ? "margin: 1.5rem 0 0" : ""}
      >
        Payment Details
      </CheckoutSectionCardHeader>
      <LegacyThemeProvider>
        <Elements stripe={stripePromise}>
          {paymentMethod === "default" && hasDefaultPaymentMethod ? (
            <PaymentFormDefaultPayment
              defaultPaymentMethod={defaultPaymentMethod}
              hasDefaultPaymentMethod={hasDefaultPaymentMethod}
              onPaymentMethodChange={onPaymentMethodChange}
              paymentMethod={paymentMethod}
              onToken={handleToken}
            />
          ) : null}
          {paymentMethod === "stripe" ? (
            <div css="margin: 1rem 0 0">
              <CheckoutStripeForm
                onToken={handleToken}
                orderTotal={orderTotal}
                error={error}
                onError={handleError}
                onSubmittingStateChange={onSubmitStateChange}
                onSubmitDisabledToggle={onDisableSubmit}
                isGuidedSignUpFunnel={true}
              />
            </div>
          ) : null}
          {paymentMethod === "paypal" ? (
            <CheckoutPayPalButton
              onToken={handleToken}
              error={error}
              onError={handleError}
              orderTotal={orderTotal}
            />
          ) : null}
        </Elements>
      </LegacyThemeProvider>
      {paymentMethod !== "paypal" ? (
        <>
          <PaymentFormLegalEnrollment
            css="margin: 1rem 0 0"
            onValuesChange={onEnrollmentValuesChange}
            plan={plan}
          />
          <div
            css={`
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 0.5rem;
              text-align: center;
              padding: 1rem 0 0;
            `}
          >
            <img
              src={assetUrl("/static/img/lock.svg")}
              alt="Secure"
              width={30}
            />
            <p>Safe and Secure with SSL</p>
          </div>
        </>
      ) : null}
    </Theme>
  );
};

type WithFormikProps = Omit<
  PaymentFormPaymentMethodProps,
  | "defaultPaymentMethod"
  | "error"
  | "handleError"
  | "handleToken"
  | "hasDefaultPaymentMethod"
  | "onPaymentMethodChange"
  | "orderTotal"
  | "paymentMethod"
  | "submitting"
  | "values"
>;

type UsePaymentProps = Omit<UsePaymentArgs, "paymentMethod">;

export interface PaymentFormPaymentMethodWithFormikProps
  extends WithFormikProps,
    UsePaymentProps {
  checkoutSession: CheckoutSession;
  paymentMethodInitialValues?: PaymentMethodValuesPartial;
  onFormIdChange?: (formId: PaymentFormId) => void;
  onSubmit?: PaymentMethodSubmit;
  onSubmitting?: (submitting: boolean) => void;
  selectedPlanPrepaidPricing?: PlanPrepaidPricing | null;
  phoneNumber?: string | null;
}

const PaymentFormPaymentMethodWithFormik = ({
  checkoutSession,
  contentfulQuizModelRaw,
  onDisableSubmit,
  onFormIdChange,
  onSubmit,
  onSubmitting,
  onPurchaseSuccess,
  onToken,
  orderSummary,
  phoneNumber,
  ...props
}: PaymentFormPaymentMethodWithFormikProps) => {
  const defaultPaymentMethod = checkoutSession?.defaultPaymentMethod;
  const hasDefaultPaymentMethod = Boolean(defaultPaymentMethod);
  const initialPaymentMethod: PaymentMethod = hasDefaultPaymentMethod
    ? "default"
    : "stripe";
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>(initialPaymentMethod);

  const [initialValues, setInitialValues] = useState<PaymentMethodValues>({
    [PaymentMethodKey.PAYMENT_METHOD]: initialPaymentMethod,
    ...props.paymentMethodInitialValues,
  });

  const paymentMethodOptions = usePayment({
    checkoutSession,
    contentfulQuizModelRaw,
    onPurchaseSuccess,
    onToken,
    orderSummary,
    paymentMethod,
    phoneNumber,
  });

  useEffect(() => {
    onSubmitting?.(paymentMethodOptions.submitting);
  }, [onSubmitting, paymentMethodOptions.submitting]);

  const [disableSubmit, setDisableSubmit] = useState(true);
  const [stripeFormComplete, setStripeFormComplete] = useState(false);

  const handleEnrollmentValuesChanged = (values: LegalEnrollmentValues) => {
    const checked = values[LegalEnrollmentKey.LEGAL_ENROLLMENT];
    const disableForDefault = paymentMethod === "default" && !checked;
    const disableForNewStripe =
      paymentMethod === "stripe" && (!checked || !stripeFormComplete);
    setDisableSubmit(disableForDefault || disableForNewStripe);
  };

  const handleDisableSubmit = (disable: boolean) => {
    setStripeFormComplete(!disable);
  };

  // disable submit if either form incomplete or legal enrollment unchecked
  useEffect(() => {
    onDisableSubmit?.(disableSubmit);
  }, [disableSubmit, onDisableSubmit]);

  useEffect(() => {
    onFormIdChange?.(PAYMENT_FORM_ID_MAP[paymentMethod]);
    setInitialValues({
      [PaymentMethodKey.PAYMENT_METHOD]: paymentMethod,
    });
  }, [onFormIdChange, paymentMethod, setInitialValues]);

  const handlePaymentMethodChange = (paymentMethod: PaymentMethod) => {
    setPaymentMethod(paymentMethod);
  };

  const handleRadioClick = (option: GuidedSignUpRadioGroupOption) => {
    const paymentMethod = option.value as PaymentMethod;
    setPaymentMethod(paymentMethod);
  };

  const handleSubmit: PaymentMethodSubmit = (values, formikHelpers) => {
    onSubmit?.(values, formikHelpers);
  };

  return (
    <Theme>
      <Formik<PaymentMethodValues>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ values }) => {
          return (
            <PaymentFormPaymentMethod
              {...props}
              {...paymentMethodOptions}
              defaultPaymentMethod={defaultPaymentMethod}
              hasDefaultPaymentMethod={hasDefaultPaymentMethod}
              onDisableSubmit={handleDisableSubmit}
              onEnrollmentValuesChange={handleEnrollmentValuesChanged}
              onPaymentMethodChange={handlePaymentMethodChange}
              onRadioClick={handleRadioClick}
              paymentMethod={paymentMethod}
              values={values}
            />
          );
        }}
      </Formik>
    </Theme>
  );
};

export default PaymentFormPaymentMethodWithFormik;
